import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

/**
 * Command palette infrastructure.
 *
 * Views register item *sources* (functions returning palette items) while they
 * are mounted; the palette collects items from every registered source each
 * time it opens. This keeps the palette decoupled from any particular view:
 * the project view contributes chapters/scenes/actions only while a project is
 * open, the dashboard contributes its own actions, and app-wide items are
 * registered once at the root.
 *
 * Item shape:
 * {
 *   id: string            — unique, stable across opens (used for recents)
 *   group: string         — section label ("Chapters", "Actions", …)
 *   title: string         — primary matched text
 *   subtitle?: string     — secondary matched text, rendered dimmed
 *   keywords?: string     — extra matchable text, never rendered
 *   icon?: ReactNode      — leading icon
 *   perform: () => void   — invoked on selection; palette closes first
 * }
 */

const CommandPaletteContext = createContext(null);

const RECENTS_STORAGE_KEY = 'plotwyze:palette:recents';
const RECENTS_LIMIT = 5;

export const readRecentIds = () => {
  try {
    const raw = window.localStorage.getItem(RECENTS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : [];
  } catch {
    return [];
  }
};

export const saveRecentId = (id) => {
  if (!id) return;
  try {
    const next = [id, ...readRecentIds().filter((existing) => existing !== id)].slice(
      0,
      RECENTS_LIMIT
    );
    window.localStorage.setItem(RECENTS_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage unavailable (private mode etc.) — recents are a nicety only.
  }
};

export const CommandPaletteProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const sourcesRef = useRef(new Map());
  const sourceOrderRef = useRef(0);
  const isLoggedIn = useSelector((state) => Boolean(state.user?.userInfo?._id));

  const registerSource = useCallback((id, getItems) => {
    sourceOrderRef.current += 1;
    sourcesRef.current.set(id, { getItems, order: sourceOrderRef.current });
    return () => {
      sourcesRef.current.delete(id);
    };
  }, []);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  /**
   * Invoke every registered source. Sources may be async; each one's items are
   * delivered through `onItems(items, sourceOrder)` as soon as they resolve so
   * a slow source (e.g. a network fetch) never blocks the rest.
   * Returns a cancel function.
   */
  const collectItems = useCallback((onItems) => {
    let cancelled = false;
    const sources = Array.from(sourcesRef.current.values());

    sources.forEach((source) => {
      Promise.resolve()
        .then(() => source.getItems())
        .then((items) => {
          if (!cancelled && Array.isArray(items) && items.length > 0) {
            onItems(items, source.order);
          }
        })
        .catch((error) => {
          // A failing source should never break the palette.
          console.warn('[CommandPalette] source failed:', error);
        });
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // Global hotkey: Ctrl+K / Cmd+K toggles the palette (logged-in users only).
  useEffect(() => {
    if (!isLoggedIn) {
      setIsOpen(false);
      return undefined;
    }
    const onKeyDown = (event) => {
      const modifier = event.ctrlKey || event.metaKey;
      if (modifier && !event.shiftKey && !event.altKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setIsOpen((wasOpen) => !wasOpen);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isLoggedIn]);

  const value = useMemo(
    () => ({ isOpen, open, close, registerSource, collectItems, isEnabled: isLoggedIn }),
    [isOpen, open, close, registerSource, collectItems, isLoggedIn]
  );

  return <CommandPaletteContext.Provider value={value}>{children}</CommandPaletteContext.Provider>;
};

CommandPaletteProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useCommandPalette = () => {
  const context = useContext(CommandPaletteContext);
  if (!context) {
    throw new Error('useCommandPalette must be used within a CommandPaletteProvider');
  }
  return context;
};

/**
 * Register a palette item source for the lifetime of the calling component.
 * `getItems` may be sync or async; the latest closure is always used, so
 * callers don't need to memoize it.
 */
export const useCommandSource = (id, getItems) => {
  const { registerSource } = useCommandPalette();
  const getItemsRef = useRef(getItems);
  getItemsRef.current = getItems;

  useEffect(() => {
    return registerSource(id, () => getItemsRef.current());
  }, [id, registerSource]);
};

// Test-only export so unit tests can reset stored recents.
export const RECENTS_KEY_FOR_TESTS = RECENTS_STORAGE_KEY;

export default CommandPaletteContext;
